/** Shared Sales Exec / TL helpers for assignment, stage, and source rules. */

export type AuthLike = {
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  hierarchyLevel?: string | number;
  role?: string;
  userName?: string;
  userId?: string;
  memberships?: Array<{ roleSlug?: string }>;
};

const TL_ROLE_RE = /\b(tl|team\s*lead|teamlead)\b/i;

/** True for Admin, Super Admin, Sales TL (role/hierarchy name), or hierarchyLevel 1. */
export function canAssignToSelf(auth: AuthLike | null | undefined): boolean {
  if (!auth) return false;
  if (auth.isAdmin || auth.isSuperAdmin) return true;
  if (String(auth.hierarchyLevel ?? "") === "1") return true;
  const role = String(auth.role || "");
  if (TL_ROLE_RE.test(role)) return true;
  const memberships = auth.memberships || [];
  if (
    memberships.some((m) =>
      TL_ROLE_RE.test(String(m?.roleSlug || ""))
    )
  ) {
    return true;
  }
  return false;
}

/** Only Admin / Super Admin / hierarchyLevel 1 may edit lead source. */
export function canEditLeadSource(auth: AuthLike | null | undefined): boolean {
  if (!auth) return false;
  if (auth.isAdmin || auth.isSuperAdmin) return true;
  return String(auth.hierarchyLevel ?? "") === "1";
}

/** Case-insensitive Not Interested / Lost stage check. */
export function isNiOrLostStage(stage: string | null | undefined): boolean {
  const s = String(stage || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!s) return false;
  if (s === "lost" || s.includes("lost")) return true;
  if (
    s === "not interested" ||
    s === "notinterested" ||
    s.includes("not interested") ||
    s.includes("notinterested")
  ) {
    return true;
  }
  return false;
}

/** Parse reminder date (DD-MM-YYYY or ISO) + optional time into a Date. */
export function parseReminderDateTime(
  dateStr?: string | null,
  timeStr?: string | null
): Date | null {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  let base: Date | null = null;

  const dmy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (dmy) {
    base = new Date(
      Number(dmy[3]),
      Number(dmy[2]) - 1,
      Number(dmy[1]),
      0,
      0,
      0,
      0
    );
  } else {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) base = parsed;
  }
  if (!base || Number.isNaN(base.getTime())) return null;

  if (timeStr) {
    const t = String(timeStr).trim();
    const ampm = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    const h24 = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (ampm) {
      let h = Number(ampm[1]) % 12;
      if (/pm/i.test(ampm[3])) h += 12;
      base.setHours(h, Number(ampm[2]), 0, 0);
    } else if (h24) {
      base.setHours(Number(h24[1]), Number(h24[2]), Number(h24[3] || 0), 0);
    }
  }
  return base;
}

export function startOfLocalDay(d = new Date()): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfLocalDay(d = new Date()): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function isReminderDueToday(
  dateStr?: string | null,
  timeStr?: string | null
): boolean {
  const dt = parseReminderDateTime(dateStr, timeStr);
  if (!dt) return false;
  const start = startOfLocalDay();
  const end = endOfLocalDay();
  return dt.getTime() >= start.getTime() && dt.getTime() <= end.getTime();
}

export function isReminderOverdue(
  dateStr?: string | null,
  timeStr?: string | null
): boolean {
  const dt = parseReminderDateTime(dateStr, timeStr);
  if (!dt) return false;
  return dt.getTime() < startOfLocalDay().getTime();
}

export function isAssignedToday(assignedAt?: string | null): boolean {
  if (!assignedAt) return false;
  const d = new Date(assignedAt);
  if (Number.isNaN(d.getTime())) return false;
  const start = startOfLocalDay();
  const end = endOfLocalDay();
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
}

export function isAssignNotification(n: {
  title?: string;
  text?: string;
  body?: string;
  type?: string;
}): boolean {
  const type = String((n as any)?.type || "").toLowerCase();
  if (type.includes("leadassigned") || type.includes("leadreassigned")) {
    return true;
  }
  const blob = `${n.title || ""} ${n.text || ""} ${n.body || ""}`.toLowerCase();
  return (
    blob.includes("lead assigned") ||
    blob.includes("reassigned") ||
    blob.includes("assigned a new lead") ||
    blob.includes("assigned to you")
  );
}

export function defaultReopenStage(stageNames: string[]): string {
  const names = (stageNames || []).map(String);
  const prefer = ["New", "Contact", "Initial", "Lead"];
  for (const p of prefer) {
    const hit = names.find(
      (n) => n.trim().toLowerCase() === p.toLowerCase()
    );
    if (hit) return hit;
  }
  return names[0] || "Contact";
}
